import { useMemo, useState } from "react";
import {
  addDays, daysBetween, monthName, nightsPhrase, shortDay, todayHere, weekdayOf,
  type RentCopy,
} from "./rent-copy";

/**
 * THE CALENDAR THAT KNOWS WHAT IT CANNOT SELL.
 *
 * Two `<input type="date">` fields were the first version, and they were wrong in
 * the way that costs a sale: the guest picked two dates, waited for a round trip,
 * and was then told the nights were gone or that the house wants two of them.
 * Every refusal arrived AFTER the choice, which is how a booking is abandoned at
 * 22.00 on a rural connection.
 *
 * So the refusals are moved in front of the tap. A day that cannot be an arrival
 * is not clickable, and it says why by how it is drawn:
 *
 *   ocupat            somebody has the night - hatched, struck through
 *   prea devreme      inside the house's notice period - faint
 *   nu se sosește     the wrong weekday for an arrival - faint
 *   ales / în interval  the stay being built - clay
 *
 * ==========================================================================
 * THE SECOND DATE IS THE ONE PEOPLE GET WRONG, so the calendar changes shape.
 * ==========================================================================
 *
 * Once an arrival is chosen, EVERY DAY UP TO AND INCLUDING IT BECOMES
 * UNCLICKABLE, the header changes to „plecarea, după 7 septembrie", and the
 * range paints itself as the guest moves. A departure is not a second
 * independent date; it is the end of a stay that has already begun, and a
 * calendar that lets you pick the 5th after choosing the 7th has taught the
 * guest nothing about what it will accept.
 *
 * IT ALSO STOPS AT THE FIRST OCCUPIED NIGHT. You may check OUT on a night
 * somebody else has - you do not sleep the night you leave - but you may not
 * book across it, and that is arithmetic the guest should never have to do.
 *
 * ONE MONTH, AT EVERY WIDTH, AND THAT IS A CORRECTION.
 *
 * It drew two side by side on a laptop, which sounded generous and looked
 * broken: this calendar lives in the 380px booking column beside the
 * description, so two seven-column grids got 169px each. Cells collapsed to
 * about 24px, 17px numerals stopped lining up with the weekday headers above
 * them, and the joined title „august 2026 · septembrie 2026" wrapped to two
 * lines while each grid ALSO repeated its own month name underneath.
 *
 * A month at a comfortable size beats two months nobody can read. The `months`
 * prop stays, because a future full-width surface (a page of its own, an
 * embedded widget) can spend the width — but the booking column asks for one.
 */

export type CalendarRules = {
  min_nights: number;
  max_nights: number;
  lead_days_min: number;
  lead_days_max: number;
  arrival_days: string;
  departure_days: string;
};

type DayState =
  | "blank"      // padding before the 1st
  | "past"       // gone, or inside the notice period, or past the horizon
  | "taken"      // somebody has this night
  | "closed"     // the house does not start (or end) a stay on this weekday
  | "short"      // would make the stay too short or too long
  | "free"
  | "start"
  | "end"
  | "between";

const MONDAY_FIRST = [1, 2, 3, 4, 5, 6, 0];

export function RangeCalendar({
  from, to, taken, rules, t, lang, months, onPick,
}: {
  from: string;
  to: string;
  /** Nights somebody already has, for THIS unit and its relatives. */
  taken: Set<string>;
  rules: CalendarRules;
  t: RentCopy;
  lang: string;
  /** How many months to draw side by side. */
  months: number;
  onPick: (from: string, to: string) => void;
}) {
  const today = todayHere();
  const [cursor, setCursor] = useState(() => (from || today).slice(0, 7));

  /* The window the house is willing to sell in, computed once. */
  const firstSellable = rules.lead_days_min > 0 ? addDays(today, rules.lead_days_min) : today;
  const lastSellable = rules.lead_days_max > 0 ? addDays(today, rules.lead_days_max) : null;

  /**
   * THE FIRST OCCUPIED NIGHT AFTER THE ARRIVAL — the wall a stay stops at.
   *
   * Null when nothing is taken ahead. Note it is a DEPARTURE the guest may still
   * choose: leaving on the morning of a night somebody else has booked is not a
   * clash, because the departing guest does not sleep it.
   */
  const wall = useMemo(() => {
    if (!from) return null;
    for (let d = from, i = 0; i < 400; i++, d = addDays(d, 1)) {
      if (taken.has(d)) return d;
    }
    return null;
  }, [from, taken]);

  const picking: "arrival" | "departure" = from && !to ? "departure" : "arrival";

  function stateOf(day: string): DayState {
    if (day === from && to) return "start";
    if (day === to) return "end";
    if (from && to && day > from && day < to) return "between";
    if (day === from) return "start";

    if (picking === "departure") {
      /*
       * EVERY DAY UP TO AND INCLUDING THE ARRIVAL IS CLOSED. This is the rule the
       * guest asked the calendar to make obvious: choose the 7th, and the 7th and
       * everything before it stop being answers.
       */
      if (day <= from) return "past";
      if (wall && day > wall) return "taken";
      const nights = daysBetween(from, day);
      if (rules.min_nights > 0 && nights < rules.min_nights) return "short";
      if (rules.max_nights > 0 && nights > rules.max_nights) return "short";
      if (!allows(rules.departure_days, day)) return "closed";
      return "free";
    }

    if (day < firstSellable) return "past";
    if (lastSellable && day > lastSellable) return "past";
    if (taken.has(day)) return "taken";
    if (!allows(rules.arrival_days, day)) return "closed";
    return "free";
  }

  function pick(day: string, state: DayState) {
    if (state !== "free" && state !== "start" && state !== "end") return;
    if (picking === "departure" && day > from) onPick(from, day);
    else onPick(day, ""); // a new stay begins; the old range is dropped
  }

  const grids = Array.from({ length: months }, (_, i) => addMonths(cursor, i));

  return (
    <div className="border border-dove-200 bg-paper">
      {/* WHAT IS BEING ASKED, in a sentence that changes. The commonest way to
          misuse a range picker is not knowing which end you are choosing. */}
      <div className="flex items-center justify-between gap-2 border-b border-dove-200 px-3 py-3">
        <button
          type="button"
          onClick={() => setCursor(addMonths(cursor, -1))}
          disabled={cursor <= today.slice(0, 7)}
          aria-label={t.prevMonth}
          className="tap flex w-11 items-center justify-center text-ink-muted disabled:opacity-25"
        >
          ‹
        </button>
        <p className="text-center" style={{ fontFamily: "var(--font-display)" }}>
          {grids.map((m) => monthName(lang, m)).join(" · ")}
        </p>
        <button
          type="button"
          onClick={() => setCursor(addMonths(cursor, 1))}
          aria-label={t.nextMonth}
          className="tap flex w-11 items-center justify-center text-ink-muted"
        >
          ›
        </button>
      </div>

      <p
        className={`px-4 py-2.5 ${picking === "departure" ? "bg-clay-50 text-ink" : "text-ink-muted"}`}
        style={{ fontSize: "var(--text-kicker)" }}
      >
        {picking === "departure"
          ? `${t.pickDeparture} ${shortDay(lang, from)}`
          : from && to
            ? `${shortDay(lang, from)} → ${shortDay(lang, to)} · ${nightsPhrase(lang, daysBetween(from, to))}`
            : t.pickArrival}
      </p>

      <div className={`grid gap-5 p-4 ${months > 1 ? "sm:grid-cols-2" : ""}`}>
        {grids.map((m) => (
          <MonthGrid
            key={m} month={m} t={t} lang={lang} today={today}
            stateOf={stateOf} onPick={pick}
            single={months === 1}
          />
        ))}
      </div>

      <ul
        className="flex flex-wrap gap-x-4 gap-y-1 border-t border-dove-200 px-4 py-2.5 text-ink-faint"
        style={{ fontSize: "var(--text-kicker)" }}
      >
        <li className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 bg-clay-500" /> {t.chosen}
        </li>
        <li className="flex items-center gap-1.5">
          {/* The swatch has to carry the SAME two signals the cell does — the
              hatch and the strike — or the legend explains a thing the guest
              cannot recognise. On white alone the hatch was invisible. */}
          <span
            className="inline-block h-3 w-3 border border-dove-200 bg-dove-100"
            style={{ backgroundImage: HATCH }}
          />{" "}
          <span style={{ textDecoration: "line-through" }}>{t.busy}</span>
        </li>
        <li className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 border border-dove-200" /> {t.freeDay}
        </li>
      </ul>
    </div>
  );
}

/*
 * THE NUMBER MUST SURVIVE THE HATCHING.
 *
 * The first version drew occupied nights with a heavy 2px/5px hatch in dove-400
 * and the date underneath became unreadable — which matters, because a guest
 * scanning for „is the 12th free" reads NUMBERS, and a cell they cannot read is
 * a cell they cannot rule out. Thin lines, wide gaps, and the date kept in a
 * darker ink than the strike-through suggests.
 */
const HATCH =
  "repeating-linear-gradient(45deg, var(--color-dove-200) 0 1px, transparent 1px 6px)";

function MonthGrid({
  month, t, lang, today, stateOf, onPick, single,
}: {
  month: string;
  t: RentCopy;
  lang: string;
  /** Passed down rather than recomputed: one clock for the whole calendar. */
  today: string;
  stateOf: (day: string) => DayState;
  onPick: (day: string, state: DayState) => void;
  single: boolean;
}) {
  const days = useMemo(() => {
    const [y, m] = month.split("-").map(Number);
    const last = new Date(Date.UTC(y!, m!, 0)).getUTCDate();
    const out: string[] = [];
    // Monday-first padding: Moldova reads a week that starts on Monday.
    const lead = MONDAY_FIRST.indexOf(weekdayOf(`${month}-01`));
    for (let i = 0; i < lead; i++) out.push("");
    for (let d = 1; d <= last; d++) out.push(`${month}-${String(d).padStart(2, "0")}`);
    return out;
  }, [month]);

  return (
    <div>
      {!single && (
        <p className="mb-1 text-center text-ink-muted" style={{ fontSize: "var(--text-kicker)" }}>
          {monthName(lang, month)}
        </p>
      )}
      <div className="grid grid-cols-7 gap-px">
        {t.weekdayShort.split(",").map((w, i) => (
          <div
            key={`${w}-${i}`}
            className="pb-2 text-center text-ink-faint"
            style={{ fontSize: "var(--text-kicker)" }}
          >
            {w}
          </div>
        ))}

        {days.map((day, i) => {
          if (!day) return <div key={`pad-${i}`} />;
          const state = stateOf(day);
          const dead = state === "past" || state === "taken" || state === "closed" || state === "short";
          const chosen = state === "start" || state === "end";
          const isToday = day === today;

          return (
            <button
              key={day}
              type="button"
              disabled={dead}
              onClick={() => onPick(day, state)}
              aria-label={`${Number(day.slice(8))} ${monthName(lang, day.slice(0, 7))}`}
              aria-pressed={chosen}
              title={
                state === "taken" ? t.busy
                  : state === "closed" ? t.closedDay
                  : state === "short" ? t.tooShort
                  : undefined
              }
              className={[
                "relative flex aspect-square items-center justify-center",
                chosen ? "bg-clay-500 text-paper" : "",
                state === "between" ? "bg-clay-100 text-ink" : "",
                state === "free" ? "text-ink hover:bg-clay-50" : "",
                state === "taken" ? "text-dove-600" : "",
                state === "past" || state === "closed" || state === "short" ? "text-dove-400" : "",
              ].join(" ")}
              style={{
                borderRadius: 0,
                minHeight: 44,
                fontSize: "var(--text-base)",
                /*
                 * TABULAR NUMERALS. Jost's proportional digits make „11" narrower
                 * than „28", so a column of dates drifts off the weekday header
                 * it belongs under — the thing that made the two-month version
                 * look broken even before the cells were too small.
                 */
                fontVariantNumeric: "tabular-nums",
                // Hatched AND struck: colour alone is not a signal on a bright
                // phone screen in a garden, which is where this is read.
                backgroundImage: state === "taken" ? HATCH : undefined,
                textDecoration: state === "taken" ? "line-through" : undefined,
                cursor: dead ? "not-allowed" : "pointer",
                // TODAY IS WHERE THE GUEST IS STANDING. Without it a calendar of
                // grey past days and black future ones has no visible hinge, and
                // the eye has to find the first black number to work out the date.
                boxShadow: isToday && !chosen ? "inset 0 0 0 1px var(--color-clay-500)" : undefined,
              }}
            >
              {Number(day.slice(8))}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Whether a weekday string like „023456" allows this date. Empty = every day. */
function allows(days: string, date: string): boolean {
  if (!days || days.length >= 7) return true;
  return days.includes(String(weekdayOf(date)));
}

/** „2026-09" plus n months, staying a valid month string. */
function addMonths(month: string, n: number): string {
  const [y, m] = month.split("-").map(Number);
  const d = new Date(Date.UTC(y!, m! - 1 + n, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}
